import { createSelector } from "reselect"
import { get, chain, pickBy, map, last } from "lodash"
import moment from "moment"
import { getStartDate, getEndDate } from "modules/security-profile/selectors/dates"
import { getDisplayType, getFactors } from "./items"
import { getConcentrationData, hasReferenceEquity } from "./concentration"
import { getBeta } from "./beta"
import { computeCategoryItemsSubset, computeCategoryItems, computeDisplayCategories, computeNonZerosKeys } from "helpers/analyze"
import { makeComputeDriftFromData } from "helpers/drift"

function getFactorLabel(factors, id) {
  const factor = get(factors, id, { label: id })
  return factor.overviewLabel || factor.label
}

export const getDisabled = state => get(state, "modules.securityProfileComposition.disabled")

export function computeData(items, beta) {
  return chain(beta)
    .map((datum, index) => {
      const { date } = datum
      const result = chain(items)
        .map(item => {
          return ([
            item.id,
            item.fn
              ? item.fn(datum)
              : get(datum, item.field, 0),
          ])
        })
        .filter(([, value]) => !Number.isNaN(value))
        .fromPairs()
        .value()

      result.date = moment(date)
      result.index = index

      return result
    })
    .filter(datum => datum)
    .value()
}

export const getSelectedRawData = createSelector(
  [getDisplayType, getBeta, getConcentrationData, hasReferenceEquity],
  (displayType, betaData, concentrationData) => displayType === "beta" ? betaData : concentrationData,
)

export const getCurrent = createSelector(
  [getSelectedRawData],
  (rawData) => last(rawData),
)

export const getAllItems = createSelector(
  [getCurrent, getDisabled, getFactors],
  (lastDatum, disabled, factors) => map(factors, (values, id) => ({
    id,
    label: getFactorLabel(factors, id),
    category: get(values, "category"),
    lastValue: get(lastDatum, ["factors", id], 0),
    field: `factors.${id}`,
    disabled: !!disabled[id],
    area: get(values, "area"),
    percentage: get(values, "percentage"),
    color: get(values, "color"),
  })),
)

export const getFilteredRawData = createSelector(
  [getSelectedRawData, getStartDate, getEndDate],
  (selectedData, startDate, endDate) =>
    selectedData.filter((data) => {
      if (data.date >= startDate && data.date <= endDate) {
        return data
      } else {
        return null
      }
    }),
)

export const getData = createSelector(
  [getAllItems, getFilteredRawData],
  (items, filteredRawData) => computeData(items, filteredRawData),
)

export const getAllCategoryItems = createSelector(
  [getAllItems, getDisplayType],
  (items, displayType) => chain(items)
    .filter(item => item.category === displayType)
    .orderBy(item => Math.abs(item.lastValue), ["desc"])
    .value(),
)

export const getOrderedCategoryItems = createSelector(
  [getAllItems, getDisplayType],
  (items, displayType) => {
    return chain(items)
      .filter(item => item.category === displayType)
      .orderBy(item => Math.abs(item.lastValue), ["asc", "desc"])
      .value()
  },
)

const getNonZerosKeys = createSelector(
  [getData],
  computeNonZerosKeys,
)

export const getFilteredData = createSelector(
  [getData, getNonZerosKeys],
  (data, keys) => data.map(item => pickBy(item, (_, key) => keys[key])),
)

export const getCategoryItemsSubset = createSelector(
  [getOrderedCategoryItems, getNonZerosKeys],
  computeCategoryItemsSubset,
)

export const getCategoryItems = createSelector(
  [getOrderedCategoryItems, getNonZerosKeys],
  computeCategoryItems,
)

export const getDisplayCategories = createSelector(
  [getAllItems, getNonZerosKeys],
  computeDisplayCategories,
)

export const getDrift = createSelector(
  [getBeta],
  makeComputeDriftFromData(item => item.factors),
)
